"use client";

import { useState, type KeyboardEvent } from "react";
import { useRouter } from "next/navigation";
import { ArrowRight } from "lucide-react";
import { trackContentPaste, trackShareImport, type PasteSurface } from "../../lib/utils/analytics";
import { parseLoneUrl, parseShareLink } from "../../lib/utils/chat-share-link";

interface PasteCtaProps {
  /** GA `surface` param for content_paste (which page took the paste). */
  surface: PasteSurface;
  /** Where to land with the text loaded, e.g. "/?to=docx". */
  to: string;
  /** Primary button text — says exactly what happens next. */
  buttonLabel: string;
  placeholder?: string;
  /** Small line under the box. */
  hint?: string;
  /** Accessible name for the textarea. */
  label?: string;
}

/**
 * Landing-page paste box. Search results for "markdown to word" and friends
 * are tool pages, not articles — a visitor expects to paste and convert on
 * the page they landed on. The box hands the text to the converter the same
 * way UploadCta does (localStorage → restored on mount) so the workspace
 * opens with the document loaded and the format pre-selected via `?to=`.
 *
 * A chat share link pasted here goes to the converter's import (the `?share=`
 * deep link), not to the editor as text — a visitor who pasted one on the PDF
 * landing got a PDF of a URL (2026-09-22). A lone link to any other site gets
 * a message instead of a conversion.
 */
export default function PasteCta({
  surface,
  to,
  buttonLabel,
  placeholder = "Paste your Markdown here — an AI reply, a README, a .md file's contents…",
  hint = "Nothing uploads when you paste. The next page shows the exact document before you download it.",
  label = "Markdown to convert",
}: PasteCtaProps) {
  const [text, setText] = useState("");
  const [notice, setNotice] = useState<string | null>(null);
  const router = useRouter();
  const ready = text.trim().length > 0;

  const go = () => {
    if (!ready) return;
    const link = parseShareLink(text);
    if (link) {
      const sep = to.includes("?") ? "&" : "?";
      router.push(`${to}${sep}share=${encodeURIComponent(link.url)}&from=${surface}`);
      return;
    }
    const url = parseLoneUrl(text);
    if (url) {
      trackShareImport("other", "unsupported_site", 0, surface);
      setNotice(
        `That is a link to ${url.hostname}. We can import ChatGPT and Gemini share links; for other sites, open the link, copy the reply with its copy button, and paste the text here.`,
      );
      return;
    }
    localStorage.setItem("markdownContent", text);
    trackContentPaste("markdown", text.length, surface);
    router.push(to);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      go();
    }
  };

  return (
    <div className="mx-auto max-w-[640px] rounded-[12px] border border-line bg-card p-3 shadow-[0_1px_0_rgba(0,0,0,0.02)]">
      <textarea
        aria-label={label}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setNotice(null);
        }}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        spellCheck={false}
        rows={7}
        className="block w-full resize-y rounded-[8px] border border-line bg-surface px-3.5 py-3 font-mono text-[13px] leading-[1.6] text-ink placeholder:text-muted focus:border-accent focus:outline-none"
      />
      {notice && (
        <p role="alert" className="mb-0 mt-2 text-[13px] leading-snug text-rose-deep">
          {notice}
        </p>
      )}
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <p className="m-0 text-[12.5px] leading-snug text-ink-3">{hint}</p>
        <button
          type="button"
          onClick={go}
          disabled={!ready}
          className="inline-flex items-center gap-2 rounded-[10px] bg-accent px-5 py-[9px] text-[13.5px] font-medium text-white transition-colors duration-150 hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-45"
        >
          {buttonLabel}
          <ArrowRight className="h-4 w-4" strokeWidth={1.75} />
        </button>
      </div>
    </div>
  );
}
